import classNames from "classnames";
import { TextAreaGripLines } from "@design-system/TextArea/TextAreaResizeGrip";
import { styleWithVars } from "@lib/cssVars";
import type { ResolvedAnatomyBinding } from "./componentAnatomy";
import {
    bindingReader,
    Part,
    type AnatomyPartHighlight,
} from "./componentAnatomyParts";
import styles from "./ComponentAnatomyView.module.scss";

/**
 * What stands inside the text area on the x-ray plate. The playground passes
 * its sandbox feature state here, so the schematic draws the same field the
 * live stage would show: a grip only when the field is resizable.
 */
export interface TextAreaSchematicContent {
    text: string;
    placeholder: string;
    isResizable: boolean;
}

const defaultContent: TextAreaSchematicContent = {
    text: "",
    placeholder: "Placeholder",
    isResizable: false,
};

const placeholderLines = ["Line two", "Line three"];

/** X-ray plate for TextArea: 320px wide, three text rows tall. */
export const TextAreaSchematic = ({
    bindings,
    highlight,
    content = defaultContent,
}: {
    bindings: readonly ResolvedAnatomyBinding[];
    highlight: AnatomyPartHighlight;
    content?: TextAreaSchematicContent;
}) => {
    const read = bindingReader(bindings);
    const radius = read("border", "border-radius");
    const lineHeight = read("label", "line-height");
    const paddingInline = read("background", "padding-inline");
    const paddingInlineEnd = content.isResizable
        ? read("background", "padding-inline-end (with grip)")
        : undefined;
    const paddingBlock = read("background", "padding-block");
    const borderWidth = read("border", "border-width");
    const ringSpread = read("focusRing", "box-shadow (spread)");
    const ringColor = read("focusRing", "box-shadow (color)");
    const gripSize = read("grip", "width");
    const gripOffset = read("grip", "offset");
    const isFilled = content.text !== "";
    const plateHeight =
        lineHeight !== undefined &&
        paddingBlock !== undefined &&
        borderWidth !== undefined
            ? `calc(3 * ${lineHeight} + 2 * (${paddingBlock}) + 2 * (${borderWidth}))`
            : undefined;
    const labelText = isFilled
        ? content.text
        : [content.placeholder, ...placeholderLines].join("\n");

    return (
        <span
            className={classNames(styles.schematic, styles.buttonSchematic)}
            style={{ height: plateHeight, width: 320 }}
        >
            {ringSpread !== undefined && ringColor !== undefined && (
                <Part
                    partId="focusRing"
                    highlight={highlight}
                    className={styles.buttonRing}
                    hitInset={`calc(-1 * ${ringSpread})`}
                    style={{
                        borderRadius: radius,
                        boxShadow: `0 0 0 ${ringSpread} ${ringColor}`,
                    }}
                />
            )}
            <Part
                partId="border"
                highlight={highlight}
                className={styles.buttonBorder}
                style={{
                    borderColor: read("border", "border-color"),
                    borderWidth: read("border", "border-width"),
                    borderRadius: radius,
                }}
            />
            <Part
                partId="background"
                highlight={highlight}
                className={styles.buttonBackground}
                style={{
                    backgroundColor: read("background", "background-color"),
                    borderRadius: radius,
                }}
            />
            <span
                className={classNames(
                    styles.buttonContent,
                    styles.inputContent,
                )}
                style={styleWithVars({
                    paddingInline,
                    paddingInlineEnd,
                    paddingBlock,
                    color: read("label", "color"),
                    alignItems: "flex-start",
                    justifyContent: "flex-start",
                    boxSizing: "border-box",
                    width: "100%",
                    height: "100%",
                    overflow: "hidden",
                })}
            >
                <Part
                    partId="label"
                    highlight={highlight}
                    className={styles.buttonLabel}
                    style={{
                        flex: 1,
                        minWidth: 0,
                        whiteSpace: "pre-line",
                        color: isFilled
                            ? read("label", "color")
                            : read("label", "color (placeholder)"),
                        fontSize: read("label", "font-size"),
                        fontWeight: read("label", "font-weight"),
                        lineHeight,
                        letterSpacing: read("label", "letter-spacing"),
                    }}
                >
                    {labelText}
                </Part>
            </span>
            {content.isResizable && (
                <Part
                    partId="grip"
                    highlight={highlight}
                    className={styles.tagSlot}
                    style={{
                        position: "absolute",
                        right: gripOffset,
                        bottom: gripOffset,
                        zIndex: 4,
                        width: gripSize,
                        height: gripSize,
                        color: read("grip", "color"),
                    }}
                >
                    <TextAreaGripLines />
                </Part>
            )}
        </span>
    );
};
